import { createRouter, createWebHistory } from 'vue-router'
import store from '@/store'
const routes = [
  {
    path: '/',
    name: 'Home',
    component: () => import(/* webpackChunkName: "Home" */ '@/pages/Home'),
    meta: { requiresAuth: true }
  },
  {
    path: '/category/selection',
    name: 'CategorySelection',
    component: () => import(/* webpackChunkName: "CategorySelection" */ '@/pages/CategorySelection'),
    meta: { requiresAuth: true }
  },
  {
    path: '/profile',
    name: 'Profile',
    component: () => import(/* webpackChunkName: "Profile" */'@/pages/Profile'),
    meta: { toTop: true, smoothScroll: true, requiresAuth: true }
  },
  {
    path: '/me/edit',
    name: 'ProfileEdit',
    component: () => import(/* webpackChunkName: "ProfileEdit" */'@/pages/Profile'),
    props: { edit: true },
    meta: { requiresAuth: true }
  },
  {
    path: '/history',
    name: 'History',
    component: () => import(/* webpackChunkName: "History" */'@/pages/HistoryShow'),
    props: { edit: true },
    meta: { requiresAuth: true }
  },
  {
    path: '/requests',
    name: 'Requests',
    component: () => import(/* webpackChunkName: "Requests" */'@/pages/requests/Requests.vue'),
    props: { edit: true },
    meta: { requiresAuth: true, excludeRole: ['bits_admin', 'superadmin'] }
  },
  {
    path: '/users',
    name: 'Users',
    component: () => import(/* webpackChunkName: "Users" */'@/pages/users/Users.vue'),
    props: { edit: true },
    meta: {
      requiresAuth: true,
      requiresPermission: ['manage_all_users', 'manage_org_users'],
      excludeRole: 'customer_user'
    }
  },
  {
    path: '/roles',
    name: 'RoleManagement',
    component: () => import(/* webpackChunkName: "RoleManagement" */'@/pages/roles/RoleManagement.vue'),
    props: { edit: true },
    meta: {
      requiresAuth: true,
      excludeRole: ['resource_manager', 'customer_user']
      // Note: Currently allowing anyone to manage roles as per user requirements except resource_manager and customer_user
    }
  },
  {
    path: '/organizations',
    name: 'Organizations',
    component: () => import(/* webpackChunkName: "Organizations" */'@/pages/construction/Construction.vue'),
    props: { edit: true },
    meta: {
      requiresAuth: true,
      requiresRole: ['bits_admin', 'superadmin']
    }
  },
  {
    path: '/settings',
    name: 'Settings',
    component: () => import(/* webpackChunkName: "Settings" */'@/pages/settings/Settings.vue'),
    props: { edit: true },
    meta: {
      requiresAuth: true,
      requiresRole: ['customer_owner']
    }
  },
  {
    path: '/category/:id',
    name: 'Category',
    component: () => import(/* webpackChunkName: "Category" */'@/pages/Category'),
    props: true,
    meta: { requiresAuth: true }
  },
  {
    path: '/document/:id',
    name: 'DocumentShow',
    component: () => import(/* webpackChunkName: "DocumentShow" */'@/pages/DocumentShow'),
    props: true,
    meta: { requiresAuth: true }
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import(/* webpackChunkName: "Dashboard" */'@/pages/Dashboard'),
    props: true,
    meta: { requiresAuth: true }
  },
  {
    path: '/verification/questions/:documentId?',
    name: 'VerificationQuestions',
    component: () => import(/* webpackChunkName: "VerificationQuestions" */'@/pages/VerificationQuestions'),
    props: true,
    meta: { requiresAuth: true }
  },
  {
    path: '/category/:categoryId/document/create',
    name: 'DocumentCreate',
    component: () => import(/* webpackChunkName: "DocumentCreate" */'@/pages/DocumentCreate'),
    props: true,
    meta: { requiresAuth: true }
  },
  {
    path: '/document/:id/delete',
    name: 'DocumentDelete',
    component: () => import(/* webpackChunkName: "DocumentDelete" */'@/pages/DocumentDelete'),
    props: true,
    meta: { requiresAuth: true }
  },
  {
    path: '/error/:id',
    name: 'Error',
    component: () => import(/* webpackChunkName: "Error" */'@/pages/Error'),
    props: true,
    meta: { requiresAuth: true }
  },
  {
    path: '/auth/login',
    name: 'AuthLogin',
    component: () => import(/* webpackChunkName: "AuthLogin" */'@/pages/auth/login/Login'),
    meta: { requiresGuest: true, layoutClass: 'login-page' }
  },
  {
    path: '/auth/confirm-signup/:email?',
    name: 'AuthConfirmSignup',
    component: () => import(/* webpackChunkName: "AuthConfirmSignup" */'@/pages/auth/confirm-signup/ConfirmSignup'),
    props: true,
    meta: { requiresGuest: true, layoutClass: 'confirm-signup-page' }
  },
  {
    path: '/auth/forgot-password',
    name: 'AuthForgotPassword',
    component: () => import(/* webpackChunkName: "AuthForgotPassword" */'@/pages/auth/forgot-password/ForgotPassword'),
    meta: { requiresGuest: true, layoutClass: 'login-page' }
  },
  {
    path: '/auth/mfa',
    name: 'AuthMfa',
    component: () => import(/* webpackChunkName: "AuthMfa" */'@/pages/auth/mfa/MFA'),
    meta: { requiresGuest: true, layoutClass: 'mfa-page' }
  },
  {
    path: '/auth/mfa-setup',
    name: 'AuthMfaSetup',
    component: () => import(/* webpackChunkName: "AuthMfaSetup" */'@/pages/auth/mfa-setup/MFASetup'),
    meta: { requiresGuest: true, layoutClass: 'mfa-setup-page' }
  },
  {
    path: '/auth/change-password',
    name: 'ChangePassword',
    component: () => import(/* webpackChunkName: "ChangePassword" */'@/pages/auth/change-password/ChangePassword'),
    meta: { layoutClass: 'change-password-page', hideNavigation: true }
  },
  {
    path: '/unauthorized',
    name: 'Unauthorized',
    component: () => import(/* webpackChunkName: "Unauthorized" */'@/pages/nopermissions/NoPermissions.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/logout',
    name: 'SignOut',
    async beforeEnter (to, from) {
      await store.dispatch('auth/signOut')
      return { name: 'Home' }
    }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import(/* webpackChunkName: "NotFound" */'@/pages/NotFound')
  }
]
const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior (to) {
    const scroll = {}
    if (to.meta.toTop) scroll.top = 0
    if (to.meta.smoothScroll) scroll.behavior = 'smooth'
    return scroll
  }
})
router.afterEach(() => {
  store.dispatch('clearItems', { modules: ['categories', 'documentsLs', 'requests', 'identifiers'] })
})

router.beforeEach(async (to, from, next) => {
  // Special handling for change-password flow (NEW_PASSWORD_REQUIRED challenge)
  const isChangingPassword = store.state.auth.changePassword

  // Block direct access to change-password page without proper authorization
  if (to.name === 'ChangePassword' && !isChangingPassword) {
    return next({ name: 'AuthLogin' })
  }

  if (to.name === 'ChangePassword' && isChangingPassword) {
    // Allow access to change-password page during password change flow
    return next()
  }

  await store.dispatch('auth/initAuthentication')
  store.dispatch('unsubscribeAllSnapshots')

  const isAuthenticated = !!store.state.auth.authId || window.sessionStorage.getItem('ACCESS_TOKEN')

  // Enhanced session validation for authenticated routes
  if (isAuthenticated && to.meta.requiresAuth) {
    const sessionValid = await store.dispatch('auth/validateSession')
    if (!sessionValid) {
      // Session was invalidated, redirect to sign-in
      return next({ name: 'AuthLogin', query: { redirectTo: to.path, reason: 'session_expired' } })
    }
  }

  let user = null
  if (isAuthenticated) {
    try {
      user = await store.dispatch('users/get')
      // CRITICAL FIX: Initialize authorization when user is authenticated
      await store.dispatch('authorization/initializeAuthorization')

      // Re-check authentication after authorization because fetchUserRole may have
      // signed out the user (e.g., inactive status). If so, redirect to login.
      const stillAuthenticated = !!store.state.auth.authId || window.sessionStorage.getItem('ACCESS_TOKEN')
      if (!stillAuthenticated && to.meta.requiresAuth) {
        return next({ name: 'AuthLogin', query: { redirectTo: to.path, reason: 'inactive_user' } })
      }
    } catch (error) {
      // User doesn't exist yet (new user flow), this is normal
      user = null
    }
  }
  const isSignedIn = store.state.auth.accessToken || window.sessionStorage.getItem('ACCESS_TOKEN')
  const isUserVerified = user?.verified?.status
  const toCompleteRegistration = !isUserVerified && to.name !== 'RegistrationCompletion'
  const toResetMFA = from.path === '/profile' && to.path === '/mfa'

  if (to.meta.requiresAuth) {
    if (!isAuthenticated && !isSignedIn) {
      return next({ name: 'AuthLogin', query: { redirectTo: to.path } })
    }

    // Check if user is basic_user - redirect to unauthorized
    const userRole = store.getters['authorization/userRole']
    if (userRole === 'basic_user' && to.name !== 'Unauthorized') {
      return next({ name: 'Unauthorized' })
    }

    // Check role exclusion
    if (to.meta.excludeRole) {
      const userRole = store.getters['authorization/userRole']
      const excludedRoles = Array.isArray(to.meta.excludeRole) ? to.meta.excludeRole : [to.meta.excludeRole]
      if (excludedRoles.includes(userRole)) {
        return next({ name: 'Unauthorized' })
      }
    }

    // Check role-based authorization
    if (to.meta.requiresRole) {
      const userRole = store.getters['authorization/userRole']
      const requiredRoles = Array.isArray(to.meta.requiresRole) ? to.meta.requiresRole : [to.meta.requiresRole]
      if (!requiredRoles.includes(userRole)) {
        return next({ name: 'Unauthorized' })
      }
    }

    // Check permission-based authorization
    if (to.meta.requiresPermission) {
      const requiredPermissions = Array.isArray(to.meta.requiresPermission) ? to.meta.requiresPermission : [to.meta.requiresPermission]
      const permissionChecks = await Promise.all(requiredPermissions.map(perm => store.dispatch('authorization/checkPermission', perm)))
      const hasAnyPermission = permissionChecks.some(hasPerm => hasPerm)
      if (!hasAnyPermission) {
        return next({ name: 'Unauthorized' })
      }
    }

    if (isUserVerified === 'declined' && to.name !== 'Error' && from.name !== 'Error') {
      return next({ name: 'Error', params: { id: 'Not generated' } })
    }

    // Only check documents for authenticated users
    const hasDocuments = (await store.dispatch('documentsLs/getItems')).length > 0
    if (!hasDocuments && isAuthenticated && isUserVerified === true && !toCompleteRegistration && !toResetMFA && isSignedIn && to.name !== 'CategorySelection' && to.name !== 'DocumentCreate') {
      return next({ name: 'CategorySelection' })
    }
    if (isUserVerified === 'pending' && to.name !== 'VerificationQuestions') {
      return next({ name: 'VerificationQuestions' })
    }
  }

  // Check if trying to access MFA routes directly without proper flow
  if (to.meta.requiresMfaFlow) {
    const isMfaFlow = store.state.auth.mfaRequired || store.state.auth.mfaSetupRequired
    const isFromProfile = from.path === '/profile'

    if (!isMfaFlow && !isFromProfile) {
      return next({ name: 'Unauthorized' })
    }
  }

  if (to.meta.requiresGuest) {
    if (isAuthenticated && isSignedIn) {
      if (toResetMFA) {
        if (to.path !== '/mfa') {
          return next({ name: 'MFASetup' })
        } else {
          return next()
        }
      }
      return next({ name: 'Home' })
    }
  }

  next()
})

export default router
